import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../backend/useUser';
import { useCredits } from '../../backend/useCredits';
import { useConfig } from '../../utils/config';
import FreeTrial from './FreeTrial';
import { useCreditsContext } from './CreditsContext';

const FREE_TRIAL_POPUP_FREQUENCY = 2 * 60 * 1000; // 2 minutes in milliseconds

interface FreeTrialTriggerProps {
  colorMode?: 'light' | 'dark';
}

const FreeTrialTrigger = ({ colorMode = 'dark' }: FreeTrialTriggerProps) => {
  const { user, loading: userLoading } = useUser();
  const { getSubscription } = useCredits();
  const config = useConfig();
  const location = useLocation();
  const { setIsFreeTrialModalOpen } = useCreditsContext();
  const [isOpen, setIsOpen] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    const checkEligibility = async () => {
      // Gate by config
      if (!config.ENABLE_FREE_TRIAL) return;
      
      // Only trigger on root /game and /art routes
      // We check for exact match or trailing slash
      const path = location.pathname.replace(/\/$/, '');
      if (path !== '/game' && path !== '/art' && path !== '/animate') return;

      // Wait for user to load
      if (userLoading) return;
      
      // If not logged in, do nothing
      if (!user) return;

      // Avoid re-checking if we already checked/opened
      if (hasChecked) return;

      try {
        const lastSeen = localStorage.getItem('free_trial_last_seen');
        const now = Date.now();

        // If lastSeen is null, this block is skipped correctly
        if (lastSeen) {
          const lastSeenTime = parseInt(lastSeen, 10);
          if (now - lastSeenTime < FREE_TRIAL_POPUP_FREQUENCY) {
            setHasChecked(true);
            return;
          }
        }

        const subscription = await getSubscription(user.id);
        // Trigger if subscription is null, undefined, or 'free'
        if (!subscription || subscription === 'free') {
          setIsOpen(true);
          localStorage.setItem('free_trial_last_seen', now.toString());
        }
      } catch (error) {
        console.error("Error checking subscription for free trial trigger:", error);
      } finally {
        setHasChecked(true);
      }
    };

    checkEligibility();
  }, [user, userLoading, config.ENABLE_FREE_TRIAL, getSubscription, hasChecked, location.pathname]);

  // Sync modal state with CreditsContext
  useEffect(() => {
    setIsFreeTrialModalOpen(isOpen);
  }, [isOpen, setIsFreeTrialModalOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <FreeTrial 
      isOpen={isOpen} 
      onClose={handleClose} 
      colorMode={colorMode} 
    />
  );
};

export default FreeTrialTrigger;